import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useServiceImageUrl, useUpdateServiceTicket, type ServiceTicketPatch } from "@/hooks/use-equipment-service";
import { SERVICE_STATUS_LABELS, type ServiceTicketStatus } from "@/lib/equipment/constants";
import type { DBEquipmentServiceTicket } from "@/lib/db-types";

interface ServiceTicketDialogProps {
  ticket: DBEquipmentServiceTicket | null;
  onOpenChange: (open: boolean) => void;
  /** Kostnad kan bare settes av admin. */
  canSetCost: boolean;
}

const selectCls = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

/** Endre en servicesak: status, verksted og (admin) kostnad. «Ferdig» gjør utstyret tilgjengelig igjen. */
export function ServiceTicketDialog({ ticket, onOpenChange, canSetCost }: ServiceTicketDialogProps) {
  const [status, setStatus] = useState<ServiceTicketStatus>("open");
  const [vendor, setVendor] = useState("");
  const [cost, setCost] = useState("");
  const update = useUpdateServiceTicket();
  const { data: imageUrl } = useServiceImageUrl(ticket?.image_path ?? null);

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setVendor(ticket.vendor ?? "");
      setCost(ticket.cost != null ? String(ticket.cost) : "");
    }
  }, [ticket]);

  if (!ticket) return null;

  const parsedCost = cost.trim() === "" ? null : Number(cost.replace(/\s/g, "").replace(",", "."));
  const costInvalid = parsedCost !== null && (!Number.isFinite(parsedCost) || parsedCost < 0);

  const save = () => {
    const patch: ServiceTicketPatch = { status, vendor: vendor.trim() || null };
    if (canSetCost) patch.cost = parsedCost;
    update.mutate(
      { id: ticket.id, itemId: ticket.item_id, patch },
      {
        onSuccess: () => {
          toast.success(status === "resolved" && !ticket.usable ? "Saken er ferdig, og utstyret er tilgjengelig igjen" : "Servicesaken er lagret");
          onOpenChange(false);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Servicesak for {ticket.item_name}</DialogTitle>
          <DialogDescription>
            {ticket.usable ? "Brukbar" : "Ikke brukbar"}: {ticket.description}
          </DialogDescription>
        </DialogHeader>
        {imageUrl && <img src={imageUrl} alt={`Skade på ${ticket.item_name}`} className="max-h-48 rounded-md border object-contain" />}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="service-status">Status</Label>
            <select id="service-status" className={selectCls} value={status} onChange={(e) => setStatus(e.target.value as ServiceTicketStatus)}>
              {(Object.keys(SERVICE_STATUS_LABELS) as ServiceTicketStatus[]).map((s) => (
                <option key={s} value={s}>{SERVICE_STATUS_LABELS[s]}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="service-vendor">Verksted</Label>
            <Input id="service-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="F.eks. Sony Service Oslo" />
          </div>
          {canSetCost && (
            <div className="space-y-1.5">
              <Label htmlFor="service-cost">Kostnad (kr)</Label>
              <Input id="service-cost" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
              {costInvalid && <p className="text-xs text-destructive">Skriv et beløp, f.eks. 1200.</p>}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={save} disabled={update.isPending || costInvalid}>{update.isPending ? "Lagrer …" : "Lagre"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
